import {
  ICommissionDetailsBudgetTracker,
  IDetailsBudgetTrackerJobDetails,
  IFinanceActivityLog,
  IItemDetailsBudgetTracker,
} from "~/utils/interface/jobDetails";
import styles from "./ViewDetailsBudget.module.scss";
import classNames from "classnames/bind";
import { useCallback, useEffect, useState } from "react";
import React from "react";
import jobDetailsApi from "~/apis/jobDetails";
import dayjs from "dayjs";
import { formatMoney, notify, toDateOnly } from "~/utils/helper";
import { ColumnType } from "~/utils/types/common";
import { BaseTable } from "~/components/common";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import { SearchIcon } from "lucide-react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { PlusIcon } from "~/assets/icons";
import BaseModalV2 from "~/components/common/base-modal-v2/BaseModalV2";

const cx = classNames.bind(styles);

interface ViewDetailsBudgetProps {
  jobId: string;
  idBudget: string;
  onAddNote?: () => void;
  onBack?: () => void;
}

const FILTER_OPTIONS = [
  {
    label: "All",
    value: "all",
  },
  {
    label: "User Created",
    value: "User Created",
  },
  {
    label: "System Generated",
    value: "System Generated",
  },
];

const ViewDetailsBudget = ({
  jobId,
  idBudget,
}: ViewDetailsBudgetProps) => {
  const [detailsBudgetTracker, setDetailsBudgetTracker] =
    useState<IDetailsBudgetTrackerJobDetails | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteError, setNoteError] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);

  const fetchDetailsBudgetTracker = useCallback(
    async (showLoading = true) => {
      try {
        if (showLoading) setIsLoading(true);
        const response = await jobDetailsApi.getDetailsBudgetTrackerJobDetails(
          jobId,
          idBudget
        );
        if (response.data?.data) {
          setDetailsBudgetTracker(response.data.data);
        }
      } catch (error: any) {
        console.error("Error fetching details budget tracker:", error.message);
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [idBudget, jobId]
  );

  useEffect(() => {
    fetchDetailsBudgetTracker();
  }, [fetchDetailsBudgetTracker]);

  const closeNoteModal = () => {
    setIsNoteModalOpen(false);
    setNoteText("");
    setNoteError("");
  };

  // #56: a note added here is a "User Created" activity of this budget (Figma: Activity → + Add note).
  const handleSaveNote = async () => {
    const description = noteText.trim();
    if (!description) {
      setNoteError("Note is required");
      return;
    }
    try {
      setIsSavingNote(true);
      await jobDetailsApi.createFinanceActivityLog(jobId, {
        description,
        budgetTracker: Number(idBudget),
      });
      notify("success", "Note added successfully!");
      closeNoteModal();
      await fetchDetailsBudgetTracker(false);
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to add note");
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleMarkPayment = async (idCommission: string) => {
    try {
      setIsLoading(true);
      const response = await jobDetailsApi.markPaymentAsPaid(
        jobId,
        idBudget,
        idCommission
      );
      if (response.data?.data) {
        setDetailsBudgetTracker(response.data.data);
      }
    } catch (error: any) {
      console.error("Error marking payment as paid:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const columnsItem: Array<ColumnType> = [
    {
      id: "description",
      labelHeader: "Description",
      width: "40%",
      minWidth: "200px",
      renderCell: (item: IItemDetailsBudgetTracker) => (
        <div className={cx("cell", "description-cell")}>
          <p className={cx("text-content")}>{item.itemName || "-"}</p>
        </div>
      ),
    },
    {
      id: "budgetDate",
      labelHeader: "Amount/Percentage",
      width: "20%",
      minWidth: "100px",
      renderCell: (item: IItemDetailsBudgetTracker) => (
        <div className={cx("cell", "amount-percentage-cell")}>
          <p className={cx("text-content")}>
            {item.unitPrice ? `$${item.unitPrice} x ${item.quantity}` : "-"}
          </p>
        </div>
      ),
    },
    {
      id: "subTotal",
      labelHeader: "Sub Total",
      width: "20%",
      minWidth: "100px",
      renderCell: (item: IItemDetailsBudgetTracker) => {
        const amount =
          Number(item.lineTotal) ||
          (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
        const price = Number(detailsBudgetTracker?.proposePrice) || 0;
        const pct = price > 0 ? ((amount / price) * 100).toFixed(2) : "0.00";

        return (
          <div className={cx("cell", "sub-total-cell")}>
            <p className={cx("text-content")}>
              {amount
                ? `${formatMoney(amount)} (${pct}%)`
                : "-"}
            </p>
          </div>
        );
      },
    },
    {
      id: "total",
      labelHeader: "Total",
      width: "20%",
      minWidth: "100px",
      renderCell: (item: IItemDetailsBudgetTracker) => {
        const amount =
          Number(item.lineTotal) ||
          (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);

        return (
          <div className={cx("cell", "total-cell")}>
            <p className={cx("text-content")}>
              {amount ? formatMoney(amount) : "-"}
            </p>
          </div>
        );
      },
    },
  ];

  const columnsCommission: Array<ColumnType> = [
    {
      id: "description",
      labelHeader: "Description",
      width: "25%",
      minWidth: "150px",
      renderCell: (item: ICommissionDetailsBudgetTracker) => (
        <div className={cx("cell", "description-commission-cell")}>
          <p className={cx("text-content")}>{item.description || "-"}</p>
        </div>
      ),
    },
    {
      id: "to",
      labelHeader: "To",
      width: "15%",
      minWidth: "90px",
      renderCell: (item: ICommissionDetailsBudgetTracker) => (
        <div className={cx("cell", "to-commission-cell")}>
          <p className={cx("text-content", "text-light")}>
            {item.toName || "-"}
          </p>
        </div>
      ),
    },
    {
      id: "percentage",
      labelHeader: "Amount/Percentage",
      width: "15%",
      minWidth: "90px",
      renderCell: (item: ICommissionDetailsBudgetTracker) => (
        <div className={cx("cell", "percentage-commission-cell")}>
          <p className={cx("text-content")}>
            {item.of === "Fixed Amount"
              ? `$${Number(item.amount || 0).toFixed(2)} (fixed)`
              : item.amountPercentage
              ? `${item.amountPercentage}% of ${item.of}`
              : "-"}
          </p>
        </div>
      ),
    },
    {
      id: "amount",
      labelHeader: "Amount",
      width: "15%",
      minWidth: "90px",
      renderCell: (item: ICommissionDetailsBudgetTracker) => (
        <div className={cx("cell", "amount-commission-cell")}>
          <p className={cx("text-content")}>${item.amount}</p>
        </div>
      ),
    },
    {
      id: "status",
      labelHeader: "Status",
      width: "15%",
      minWidth: "90px",
      renderCell: (item: ICommissionDetailsBudgetTracker) => (
        <div className={cx("cell", "status-commission-cell")}>
          <p
            className={cx(
              "text-content",
              "text-status",
              `${item.status?.toLowerCase()}`
            )}
          >
            {item.status === "UNPAID"
              ? "Unpaid"
              : item.status === "PAID"
                ? "Paid"
                : "-"}
          </p>
        </div>
      ),
    },
    {
      id: "manage",
      labelHeader: "Manage",
      width: "15%",
      minWidth: "90px",
      renderCell: (item: ICommissionDetailsBudgetTracker) => (
        <div className={cx("cell", "action-commission-cell")}>
          <button
            className={cx("manage-button", "text-light")}
            onClick={() => handleMarkPayment(item.id)}
          >
            {item.status === "UNPAID" ? "Mark Paid" : "Mark Unpaid"}
          </button>
        </div>
      ),
    },
  ];

  const itemsTotal = React.useMemo(() => {
    if (!detailsBudgetTracker?.items?.length) return 0;
    return detailsBudgetTracker.items.reduce(
      (sum, item) =>
        sum +
        (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0),
      0
    );
  }, [detailsBudgetTracker?.items]);

  const commissionsTotal = React.useMemo(() => {
    if (!detailsBudgetTracker?.commissions?.length) return 0;
    return detailsBudgetTracker.commissions.reduce(
      (sum, c) => sum + (Number(c.amount) || 0),
      0
    );
  }, [detailsBudgetTracker?.commissions]);

  const renderProfit = (value?: string | number | null) => {
    if (!value) return "-";
    const price = Number(detailsBudgetTracker?.proposePrice);
    const pct = price ? ((Number(value) / price) * 100).toFixed(2) : "0.00";
    return (
      <span className={cx({ "text-red-500": Number(value) < 0 })}>
        {formatMoney(value)} ({pct}%)
      </span>
    );
  };

  const filteredActivities = React.useMemo(() => {
    if (!detailsBudgetTracker?.financeActivityLogs) return [];

    const query = searchQuery.toLowerCase().trim();

    return [...detailsBudgetTracker.financeActivityLogs]
      .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
      .filter((activity) => {
      if (filterType !== "all" && activity.activityType !== filterType) {
        return false;
      }

      if (!query) return true;

      const textMatch = activity.description?.toLowerCase().includes(query);
      const postedByMatch = activity.createdBy?.name?.toLowerCase().includes(query);
      const dateMatch = dayjs(activity.createdAt)
        .format("MM-DD-YYYY hh:mm A")
        .toLowerCase()
        .includes(query);

      return textMatch || postedByMatch || dateMatch;
    });
  }, [detailsBudgetTracker?.financeActivityLogs, searchQuery, filterType]);

  const columnsActivity: Array<ColumnType> = [
    {
      id: "activityType",
      labelHeader: "Posted on",
      width: "35%",
      minWidth: "175px",
      renderCell: (item: IFinanceActivityLog) => (
        <div className={cx("cell", "activity-type-cell")}>
          <p className={cx("text-content")}>
            {item !== null && item !== undefined
              ? dayjs(item.createdAt).format("MM-DD-YYYY hh:mm A")
              : "-"}
          </p>
          <p className={cx("text-content", "text-light")}>
            {item.createdBy.name || "-"}
          </p>
        </div>
      ),
    },
    {
      id: "text",
      labelHeader: "Text",
      width: "65%",
      minWidth: "325px",
      renderCell: (item: IFinanceActivityLog) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{item.description || "-"}</p>
        </div>
      ),
    },
  ];

  return (
    <div className={cx("view-details-budget-container")}>
      <div className={cx("header")}>
        <h3 className={cx("header-title")}>View Details</h3>
      </div>

      {isLoading ? (
        <div className={cx("loading-overlay")}>
          <LoadingMainContent />
        </div>
      ) : (
        <div className={cx("body")}>
          <div className={cx("body-section", "info-section")}>
            <div className={cx("info-item")}>
              <h4 className={cx("info-item-title")}>Budget Information</h4>
              <div className={cx("info-item-content")}>
                <div className={cx("info-item-content-item")}>
                  <span className={cx("info-item-content-item-label")}>
                    Number
                  </span>
                  <span className={cx("info-item-content-item-separator")}>
                    :
                  </span>
                  <span className={cx("info-item-content-item-value")}>
                    {detailsBudgetTracker?.number
                      ? detailsBudgetTracker?.number
                      : "-"}
                  </span>
                </div>
                <div className={cx("info-item-content-item")}>
                  <span className={cx("info-item-content-item-label")}>
                    Proposed Price
                  </span>
                  <span className={cx("info-item-content-item-separator")}>
                    :
                  </span>
                  <span className={cx("info-item-content-item-value")}>
                    {detailsBudgetTracker?.proposePrice
                      ? `$${detailsBudgetTracker?.proposePrice}`
                      : "-"}
                  </span>
                </div>

                <div className={cx("info-item-content-item")}>
                  <span className={cx("info-item-content-item-label")}>
                    Gross
                  </span>
                  <span className={cx("info-item-content-item-separator")}>
                    :
                  </span>
                  <span className={cx("info-item-content-item-value")}>
                    {renderProfit(detailsBudgetTracker?.gross)}
                  </span>
                </div>

                <div className={cx("info-item-content-item")}>
                  <span className={cx("info-item-content-item-label")}>
                    Net
                  </span>
                  <span className={cx("info-item-content-item-separator")}>
                    :
                  </span>
                  <span className={cx("info-item-content-item-value")}>
                    {renderProfit(detailsBudgetTracker?.net)}
                  </span>
                </div>
              </div>
            </div>

            <div className={cx("info-item")}>
              <h4 className={cx("info-item-title")}>Workflow</h4>
              <div className={cx("info-item-content")}>
                <div className={cx("info-item-content-item")}>
                  <span className={cx("info-item-content-item-label")}>
                    Budget Date
                  </span>
                  <span className={cx("info-item-content-item-separator")}>
                    :
                  </span>
                  <span className={cx("info-item-content-item-value")}>
                    {detailsBudgetTracker?.budgetDate
                      ? dayjs(toDateOnly(detailsBudgetTracker?.budgetDate)).format(
                        "MM/DD/YYYY"
                      )
                      : "-"}
                  </span>
                </div>
                <div className={cx("info-item-content-item")}>
                  <span className={cx("info-item-content-item-label")}>
                    Job
                  </span>
                  <span className={cx("info-item-content-item-separator")}>
                    :
                  </span>
                  <span
                    className={cx("info-item-content-item-value", "link-text")}
                  >
                    {detailsBudgetTracker?.job
                      ? `${detailsBudgetTracker?.job?.customerName} - ${detailsBudgetTracker?.job?.uniqueCode}`
                      : "-"}
                  </span>
                </div>

                <div className={cx("info-item-content-item")}>
                  <span className={cx("info-item-content-item-label")}>
                    Is Archived?
                  </span>
                  <span className={cx("info-item-content-item-separator")}>
                    :
                  </span>
                  <span className={cx("info-item-content-item-value")}>
                    {detailsBudgetTracker?.isArchived === undefined ||
                      detailsBudgetTracker?.isArchived === null
                      ? "-"
                      : detailsBudgetTracker.isArchived
                        ? "Yes"
                        : "No"}
                  </span>
                </div>

                {detailsBudgetTracker?.createdByAccount && (
                  <div className={cx("info-item-content-item")}>
                    <p className={cx("info-item-content-item-value")}>
                      Created By{" "}
                      <span className={cx("link-text")}>
                        {detailsBudgetTracker.createdByAccount.name}
                      </span>{" "}
                      on{" "}
                      {dayjs(
                        detailsBudgetTracker.createdByAccount.createdAt
                      ).format("MM/DD/YYYY hh:mm A")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={cx("body-section")}>
            <div className={cx("table-with-total")}>
              <BaseTable
                data={detailsBudgetTracker?.items || []}
                showPagination={false}
                columns={columnsItem}
                className={cx("table")}
              />
              {detailsBudgetTracker?.items?.length ? (
                <div className={cx("table-total-row")}>
                  {columnsItem.map((col, idx) => (
                    <div
                      key={col.id as string}
                      className={cx(
                        "table-total-cell",
                        idx === columnsItem.length - 1 && "table-total-cell-value"
                      )}
                    >
                      {idx === columnsItem.length - 1
                        ? <p className={cx("text-content")}>Total: {formatMoney(itemsTotal)}</p>
                        : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className={cx("table-footer", "mb-[36px]")}>
              <span className={cx("table-footer-item-label")}>Gross:</span>
              <span className={cx("table-footer-item-value")}>
                {renderProfit(detailsBudgetTracker?.gross)}
              </span>
            </div>

            <div className={cx("table-with-total")}>
              <BaseTable
                data={detailsBudgetTracker?.commissions || []}
                showPagination={false}
                columns={columnsCommission}
                className={cx("table")}
              />
              {detailsBudgetTracker?.commissions?.length ? (
                <div className={cx("table-total-row")}>
                  {columnsCommission.map((col, idx) => (
                    <div
                      key={col.id as string}
                      className={cx(
                        "table-total-cell",
                        col.id === "amount" && "table-total-cell-value"
                      )}
                    >
                      {col.id === "amount"
                        ? <p className={cx("text-content")}>Total: {formatMoney(commissionsTotal)}</p>
                        : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className={cx("table-footer")}>
              <span className={cx("table-footer-item-label")}>Net:</span>
              <span className={cx("table-footer-item-value")}>
                {renderProfit(detailsBudgetTracker?.net)}
              </span>
            </div>
          </div>

          <div className={cx("body-section")}>
            <div className={cx("activity-container")}>
              <div className={cx("header")}>
                <h3 className={cx("title")}>Activity</h3>

                <div className={cx("actions")}>
                  <PrimarySelectInput
                    name="invoiceActivity"
                    options={FILTER_OPTIONS}
                    placeholder="Select Filter"
                    value={
                      FILTER_OPTIONS.find(
                        (option) => option.value === filterType
                      )?.label || ""
                    }
                    onChange={(value) => setFilterType(value)}
                    className={cx("select-input")}
                  />

                  <div className={cx("search-container")}>
                    <SearchIcon className={cx("search-icon")} />
                    <input
                      type="text"
                      placeholder="Search"
                      className={cx("search-input")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>

                  <PrimaryButton
                    title="Add note"
                    icon={<PlusIcon className={cx("plus-icon")} />}
                    onClick={() => setIsNoteModalOpen(true)}
                    className={cx("add-activity-button")}
                  />
                </div>
              </div>

              <BaseTable
                data={filteredActivities}
                showPagination={false}
                columns={columnsActivity}
                className={cx("table")}
              />
            </div>
          </div>
        </div>
      )}

      <BaseModalV2
        open={isNoteModalOpen}
        onClose={closeNoteModal}
        title="Add Note"
        width="90%"
        maxWidth="500px"
      >
        <div className={cx("add-note-modal")}>
          <label className={cx("add-note-label")}>
            Note <span className="text-red-500">*</span>
          </label>
          <textarea
            className={cx("add-note-textarea", { error: !!noteError })}
            placeholder="Enter note"
            value={noteText}
            maxLength={1000}
            onChange={(e) => {
              setNoteText(e.target.value);
              setNoteError("");
            }}
          />
          {noteError && <p className={cx("add-note-error")}>{noteError}</p>}
          <div className={cx("add-note-actions")}>
            <PrimaryButton
              title="Cancel"
              onClick={closeNoteModal}
              className={cx("add-note-cancel")}
              disabled={isSavingNote}
            />
            <PrimaryButton
              title="Save"
              onClick={handleSaveNote}
              loading={isSavingNote}
            />
          </div>
        </div>
      </BaseModalV2>
    </div>
  );
};

export default ViewDetailsBudget;
